import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';
import Seo from '@/components/Seo';

export default function LoginPage({ role = 'customer', redirect = '/', title = 'Accedi' }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const setAuth = useAuthStore(s => s.setAuth);
  const nav = useNavigate();
  const loc = useLocation();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      if (role && res.data.user.role !== role) {
        toast.error(`Questo login è riservato a: ${role}`);
        setLoading(false); return;
      }
      setAuth(res.data.access_token, res.data.user);
      toast.success(`Ciao ${res.data.user.name}!`);
      const dest = loc.state?.from || redirect;
      nav(dest, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Login fallito');
    } finally { setLoading(false); }
  };

  const badge = role === 'admin' ? { label: 'Area Admin', color: 'bg-[--brand-primary] text-white' }
              : role === 'rider' ? { label: 'Area Rider', color: 'bg-[--brand-accent] text-white' }
              : { label: 'Area Cliente', color: 'bg-[--brand-surface-2] text-[--brand-primary]' };

  return (
    <div className="min-h-screen bg-[--brand-cream] noise-bg">
      <Seo title={title} description="Accedi al tuo account MōMA • Pokè." path="/login" noindex />
      <div className="max-w-md mx-auto px-4 py-10">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <Logo variant="compact" />
        </Link>

        <div className="rounded-2xl bg-white border border-[--brand-border] p-6 shadow-[var(--shadow-sm)]">
          <span className={`inline-block ${badge.color} text-xs font-semibold px-2 py-1 rounded-full mb-2`}>{badge.label}</span>
          <h1 className="font-display text-2xl font-bold">{title}</h1>
          <p className="text-sm text-[--brand-muted] mt-1">Inserisci le tue credenziali</p>

          <form onSubmit={onSubmit} className="space-y-3 mt-4">
            <div>
              <Label>Email</Label>
              <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="rounded-xl" data-testid="login-email-input"/>
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="rounded-xl" data-testid="login-password-input"/>
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]" data-testid="login-submit-button">
              {loading ? <Loader2 size={16} className="animate-spin"/> : <><LogIn size={16} className="mr-2"/> Accedi</>}
            </Button>
          </form>

          {role === 'customer' && (
            <p className="text-sm text-[--brand-muted] mt-4 text-center">
              Non hai un account? <Link to="/register" className="text-[--brand-primary] font-semibold hover:underline">Registrati</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
